import { getBootstrapModal } from './bootstrap-runtime';

export type NaisAjaxErrorPayload = {
    error?: boolean;
    errorCode?: number;
    errorTitle?: string;
    errorMessage?: string;
};

const MODAL_ID = 'nais-ajax-error-modal';

function fallbackCopy(code: number): { title: string; message: string } {
    if (code === 403) {
        return {
            title: 'Přístup odepřen',
            message: 'Nemáte oprávnění zobrazit požadovanou stránku nebo provést tuto akci.',
        };
    }
    if (code === 404) {
        return {
            title: 'Stránka nebyla nalezena',
            message: 'Požadovaná stránka neexistuje, byla přesunuta, nebo je adresa zadaná chybně.',
        };
    }
    return {
        title: 'Došlo k chybě serveru',
        message:
            'Omlouváme se, při zpracování požadavku nastala neočekávaná chyba. O chybě se automaticky dozvíme a co nejdříve ji opravíme. V případě potřeby kontaktujte správce systému.',
    };
}

function ensureModal(): HTMLElement {
    let modal = document.getElementById(MODAL_ID);
    if (modal) {
        return modal;
    }

    modal = document.createElement('div');
    modal.id = MODAL_ID;
    modal.className = 'modal fade nais-ajax-error';
    modal.tabIndex = -1;
    modal.setAttribute('aria-hidden', 'true');
    modal.innerHTML = `
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content nais-ajax-error__content">
                <div class="modal-header nais-ajax-error__header">
                    <span class="nais-ajax-error__code" data-nais-ajax-error-code></span>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Zavřít"></button>
                </div>
                <div class="modal-body nais-ajax-error__body">
                    <h2 class="nais-ajax-error__title" data-nais-ajax-error-title></h2>
                    <p class="nais-ajax-error__message" data-nais-ajax-error-message></p>
                </div>
                <div class="modal-footer nais-ajax-error__footer">
                    <a class="btn btn-outline-secondary" href="/" data-nais-ajax-error-home>Zpět do administrace</a>
                    <button type="button" class="btn btn-primary" data-bs-dismiss="modal">Rozumím</button>
                </div>
            </div>
        </div>
    `;
    document.body.appendChild(modal);
    return modal;
}

function parsePayload(raw: unknown): NaisAjaxErrorPayload | null {
    if (!raw || typeof raw !== 'object') {
        return null;
    }
    const payload = raw as NaisAjaxErrorPayload;
    return payload.error ? payload : null;
}

export function showNaisAjaxError(payload: NaisAjaxErrorPayload = {}): void {
    const code = Number(payload.errorCode) || 500;
    const fallback = fallbackCopy(code);
    const title = (payload.errorTitle || '').trim() || fallback.title;
    const message = (payload.errorMessage || '').trim() || fallback.message;

    const modalEl = ensureModal();
    const codeEl = modalEl.querySelector<HTMLElement>('[data-nais-ajax-error-code]');
    const titleEl = modalEl.querySelector<HTMLElement>('[data-nais-ajax-error-title]');
    const messageEl = modalEl.querySelector<HTMLElement>('[data-nais-ajax-error-message]');
    if (codeEl) {
        codeEl.textContent = `Chyba ${code}`;
    }
    if (titleEl) {
        titleEl.textContent = title;
    }
    if (messageEl) {
        messageEl.textContent = message;
    }

    getBootstrapModal().getOrCreateInstance(modalEl).show();
}

function payloadFromXhr(xhr: JQuery.jqXHR): NaisAjaxErrorPayload | null {
    const json = (xhr as JQuery.jqXHR & { responseJSON?: unknown }).responseJSON;
    const fromJson = parsePayload(json);
    if (fromJson) {
        return fromJson;
    }

    const text = xhr.responseText?.trim();
    if (!text) {
        return null;
    }
    try {
        return parsePayload(JSON.parse(text));
    } catch {
        return null;
    }
}

/** nette.ajax extension — show modal for application error payloads and HTTP failures. */
export function initAjaxErrorHandler(): void {
    const $ = window.jQuery;
    if (!$?.nette) {
        return;
    }

    $.nette.ext('naisAjaxError', {
        success(payload: unknown) {
            const errorPayload = parsePayload(payload);
            if (!errorPayload) {
                return;
            }
            showNaisAjaxError(errorPayload);
            return false;
        },
        error(_xhr: JQuery.jqXHR, _status: string, _error: string, settings: { nette?: unknown }) {
            // Ignore non-nette requests (raw $.ajax helpers).
            if (!settings?.nette) {
                return;
            }
            const fromPayload = payloadFromXhr(_xhr);
            if (fromPayload) {
                showNaisAjaxError(fromPayload);
                return;
            }
            const statusCode = _xhr.status || 500;
            showNaisAjaxError({
                error: true,
                errorCode: statusCode >= 400 && statusCode < 600 ? statusCode : 500,
            });
        },
    });

    window.showNaisAjaxError = showNaisAjaxError;
}
